import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../home/Navbar';
import Footer from '../../home/Footer';
import Loader from '../Loader';
import Queryform from '../Queryform';
import { API_URL, API_END_POINT } from '../Utils/Constant';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import BlogRecommendations from './BlogRecommendations';

const BlogPage = () => {
    const { blogTitle } = useParams(); // Extract the blog title from the route
    const location = useLocation();
    const navigate = useNavigate();

    // Extract the blog ID from the query string
    const queryParams = new URLSearchParams(location.search);
    const blogId = queryParams.get('id');

    const [blog, setBlog] = useState(null);
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const scrollContainer = useRef(null);

    const scrollLeft = () => {
        if (scrollContainer.current) {
            const scrollAmount = window.innerWidth < 768 ? 300 : 400;
            scrollContainer.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (scrollContainer.current) {
            const scrollAmount = window.innerWidth < 768 ? 300 : 400;
            scrollContainer.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        if (!blogId) {
            setError('Invalid blog ID');
            setLoading(false);
        } else {
            fetchBlog();
        }
    }, [blogId]);

    const fetchBlog = async () => {
        try {
            const response = await fetch(`${API_END_POINT}astroblog/${blogId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch blog');
            }
            const data = await response.json();
            setBlog(data);
            // Fetch other blogs after getting the main blog
            fetchOtherBlogs(data._id);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const fetchOtherBlogs = async (currentBlogId) => {
        try {
            const response = await fetch(`${API_END_POINT}astroblog/`);
            if (!response.ok) {
                throw new Error('Failed to fetch other blogs');
            }
            const data = await response.json();
            // Filter out the current blog and get all other blogs
            const otherBlogs = data.filter(blog => blog._id !== currentBlogId);
            setRelatedBlogs(otherBlogs);
        } catch (err) {
            console.error('Error fetching other blogs:', err);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-red-500 text-xl">Error: {error}</div>
            </div>
        );
    }

    if (!blog) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-500 text-xl">Blog not found</div>
            </div>
        );
    }

    return (
        <>
            <section className="bg-gradient-to-r from-white to-success/40 text-gray-900 min-h-screen">
                <div>
                    <Navbar />
                </div>
                <div className='text-gray-900 flex flex-col items-center py-8'>
                    <h1 className="text-4xl text-secondary font-serif font-bold mb-2">Astro Blog</h1>
                    <p className="text-lg mb-2 text-secondary font-serif text-center max-w-2xl">
                        Read our latest blog posts and stay updated with the latest news and trends in the astrology world.
                    </p>
                </div>
                <div className="max-w-5xl mx-auto px-4 py-1">
                    <button
                        onClick={() => navigate('/blog')}
                        className="mb-6 text-secondary hover:text-secondary/80 flex items-center gap-2"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M10 19l-7-7m0 0l7-7m-7 7h18"
                            />
                        </svg>
                        Back to Blogs
                    </button>

                    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                        <img
                            src={blog.image}
                            alt={blog.title}
                            className="w-full h-96 object-fit"
                        />
                        <div className="p-8">
                            <div className="mb-4">
                                <span className="bg-secondary text-white px-3 py-1 rounded-full text-sm">
                                    {blog.category}
                                </span>
                            </div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-4">{blog.title}</h1>
                            <div className="flex items-center gap-4 text-gray-600 mb-6">
                                <span>By {blog.author}</span>
                                <span>•</span>
                                <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                            </div>
                            <div className="prose max-w-none">
                                {blog.content.split('\r\n\r\n').map((paragraph, index) => (
                                    <p key={index} className="mb-4 text-gray-700 leading-relaxed">
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Other Blogs Section */}
                <BlogRecommendations />
                <Queryform />
                <Footer />
            </section>
        </>
    );
};

export default BlogPage;